import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from "../../utils/Enums";
import { apiClient } from "../../utils/ApiClient";
import {InsuranceRate} from "../../models/InsuranceRate";
import {NewInsuranceRateFormProps} from "../../components/insuranceRates/NewInsuranceRateScreen";
import {ModifyInsuranceRateFormProps} from "../../components/insuranceRates/ModifyInsuranceRateScreen";

interface InsuranceRatesState {
    insuranceRates: InsuranceRate[];
    currentInsuranceRate?: InsuranceRate | null;
    getInsuranceRatesStatus: RequestStatus;
    getCurrentInsuranceRate: RequestStatus;
    createInsuranceRateStatus: RequestStatus;
    updateInsuranceRateStatus: RequestStatus;
    deleteInsuranceRateStatus: RequestStatus;
    errors: string[];
}

const initialState: InsuranceRatesState = {
    insuranceRates: [],
    currentInsuranceRate: null,
    getInsuranceRatesStatus: RequestStatus.Idle,
    getCurrentInsuranceRate: RequestStatus.Idle,
    createInsuranceRateStatus: RequestStatus.Idle,
    updateInsuranceRateStatus: RequestStatus.Idle,
    deleteInsuranceRateStatus: RequestStatus.Idle,
    errors: [],
};

export const getInsuranceRates = createAsyncThunk(
    'InsuranceRates/getInsuranceRates',
    async (_, { rejectWithValue }) => {
        const response = await apiClient.get('insurance-rates');
        return response.data;
    }
);

export const createInsuranceRate = createAsyncThunk(
    'InsuranceRates/createInsuranceRate',
    async (data: NewInsuranceRateFormProps, { rejectWithValue }) => {
        const response = await apiClient.post('insurance-rates', data);
        return response.data;
    }
);

export const getCurrentInsuranceRate = createAsyncThunk(
    'InsuranceRates/getCurrentInsuranceRate',
    async ({ id }: { id: string | undefined }, { rejectWithValue }) => {
        const response = await apiClient.get(`insurance-rates/${id}`);
        return response.data;
    }
);

export const updateInsuranceRate = createAsyncThunk(
    'InsuranceRates/updateInsuranceRate',
    async ({ id, data }: { id: string | undefined, data: ModifyInsuranceRateFormProps }, { rejectWithValue }) => {
        const response = await apiClient.put(`insurance-rates/${id}`, data);
        return response.data;
    }
);

export const deleteInsuranceRate = createAsyncThunk(
    'InsuranceRates/deleteInsuranceRate',
    async ({ id }: { id: string | undefined }, { rejectWithValue }) => {
        const response = await apiClient.delete(`insurance-rates/${id}`);
        return response.data;
    }
);

const InsuranceRatesSlice = createSlice({
    name: 'InsuranceRates',
    initialState,
    reducers: {
        resetCreateInsuranceRateStatus: (state) => {
            state.createInsuranceRateStatus = RequestStatus.Idle;
            state.currentInsuranceRate = null;
        },
        resetUpdateInsuranceRateStatus: (state) => {
            state.updateInsuranceRateStatus = RequestStatus.Idle;
            state.currentInsuranceRate = null;
        },
        resetDeleteInsuranceRateStatus: (state) => {
            state.deleteInsuranceRateStatus = RequestStatus.Idle;
            state.currentInsuranceRate = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getInsuranceRates.pending, (state, action) => {
                state.getInsuranceRatesStatus = RequestStatus.Loading;
            })
            .addCase(getInsuranceRates.fulfilled, (state, action) => {
                state.getInsuranceRatesStatus = RequestStatus.Success;
                state.insuranceRates = action.payload;
            })
            .addCase(getInsuranceRates.rejected, (state, action) => {
                state.getInsuranceRatesStatus = RequestStatus.Failed;
            })

            .addCase(createInsuranceRate.pending, (state, action) => {
                state.createInsuranceRateStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(createInsuranceRate.fulfilled, (state, action) => {
                state.createInsuranceRateStatus = RequestStatus.Success;
            })
            .addCase(createInsuranceRate.rejected, (state, action) => {
                state.createInsuranceRateStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(updateInsuranceRate.pending, (state, action) => {
                state.updateInsuranceRateStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateInsuranceRate.fulfilled, (state, action) => {
                state.updateInsuranceRateStatus = RequestStatus.Success;
            })
            .addCase(updateInsuranceRate.rejected, (state, action) => {
                state.updateInsuranceRateStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(deleteInsuranceRate.pending, (state, action) => {
                state.deleteInsuranceRateStatus = RequestStatus.Loading;
            })
            .addCase(deleteInsuranceRate.fulfilled, (state, action) => {
                state.deleteInsuranceRateStatus = RequestStatus.Success;
            })
            .addCase(deleteInsuranceRate.rejected, (state, action) => {
                state.deleteInsuranceRateStatus = RequestStatus.Failed;
            })

            .addCase(getCurrentInsuranceRate.pending, (state, action) => {
                state.getCurrentInsuranceRate = RequestStatus.Loading;
            })
            .addCase(getCurrentInsuranceRate.fulfilled, (state, action) => {
                state.getCurrentInsuranceRate = RequestStatus.Success;
                state.currentInsuranceRate = action.payload;
            })
            .addCase(getCurrentInsuranceRate.rejected, (state, action) => {
                state.getCurrentInsuranceRate = RequestStatus.Failed;
            });
    },
});

export const {
    resetCreateInsuranceRateStatus,
    resetUpdateInsuranceRateStatus,
    resetDeleteInsuranceRateStatus
} = InsuranceRatesSlice.actions;

export default InsuranceRatesSlice.reducer;